import { div, span, anchor } from "../../shared/dom";
import { Overlay, OverlayStyle } from "../../shared/overlay";
import { ApiTeam, ApiTeamRole, ApiTeamAddUsersRequest, ApiUser } from "./api_data";
import { App } from "./app";
import { Ajax } from "../../shared/ajax";
import { v4 as uuid } from 'uuid';
import { DropdownOption, viewDropdown, DropdownOptionType } from "../../shared/dropdown";

export interface TeamForm {
  name_field: HTMLInputElement;
  container: HTMLDivElement;
  button: HTMLElement;
}

export class Teams {

  static teamOverlayBody(): TeamForm {
    const d = div("overlay-panel-body");
    d.style.maxWidth = (window.innerWidth - 200) + "px";

    const settings = div("settings-group");
    const name = div("section");
    const name_label = div("settings-label");
    name_label.innerHTML = "Team Name";
    const name_value = div("settings-value");
    const name_input = document.createElement("input");
    name_input.value = "";
    name_value.appendChild(name_input);
    name.appendChild(name_label);
    name.appendChild(name_value);
    settings.appendChild(name);
    d.appendChild(settings);

    const panel_footer = div("overlay-panel-footer");
    panel_footer.appendChild(div("spacer"));
    const save_button = document.createElement("a");
    save_button.classList.add("button", "noselect");
    save_button.innerHTML = "Create";
    panel_footer.appendChild(save_button);
    d.appendChild(panel_footer);
    return {
      container: d,
      name_field: name_input,
      button: save_button,
    };
  }

  static newTeamOverlay(): Overlay {

    const body = Teams.teamOverlayBody();

    const save = function () {
      if (!/\S/.test(body.name_field.value)) {
        return;
      }
      if (!App.user) {
        return;
      }
      const t: ApiTeam = {
        id: uuid(),
        owner_id: App.user.id,
        name: body.name_field.value,
        deleted_at: null,
        roles: [],
      };
      const url = "/api/v1/teams/" + t.id;
      Ajax.put(url, function (team: ApiTeam) {
        Overlay.close();
        App.teams.push(team);
        App.render();
      }, t);
    };

    body.name_field.onkeydown = function (e) {
      if (e.keyCode === 13) { // return
        save();
      }
    };

    body.button.onclick = function () {
      save();
    };

    const overlay = new Overlay(OverlayStyle.Viz, "New Team", [body.container], "Create", function () {
      save();
    }, undefined, function () {
      body.name_field.focus();
    });
    return overlay;
  }

  static editTeamOverlay(team: ApiTeam): Overlay {

    const body = this.teamOverlayBody();
    body.name_field.value = team.name;
    body.button.innerText = "Update";

    const save = function () {
      if (!/\S/.test(body.name_field.value)) {
        return;
      }
      const changes: ApiTeam = {
        id: team.id,
        owner_id: team.owner_id,
        name: body.name_field.value,
        deleted_at: null,
        roles: [],
      };
      const url = "/api/v1/teams/" + changes.id;
      Ajax.put(url, function (updated_team: ApiTeam) {
        Overlay.close();
        for (let i = 0; i < App.teams.length; i++) {
          const el = App.teams[i];
          if (el.id === team.id) {
            App.teams[i] = updated_team;
          }
        }
        App.render();
      }, changes);
    };

    body.name_field.onkeydown = function (e) {
      if (e.keyCode === 13) { // return
        save();
      }
    };

    body.button.onclick = function () {
      save();
    };

    const overlay = new Overlay(OverlayStyle.Viz, "Edit Team", [body.container], "Update", function () {
      save();
    }, undefined, function () {
      body.name_field.focus();
    });
    return overlay;
  }

  static deleteTeamOverlay(team: ApiTeam): Overlay {
    const message = "This will permanently delete the team <b>" + team.name + "</b>. Everything in the team will also be deleted for all users.";
    const confirm = "To confirm, type the name of the team below.";
    const overlay = Overlay.deleteConfirmByTypingOverlay("Delete Team", message, confirm, team.name, function () {
      const changes: ApiTeam = {
        id: team.id,
        owner_id: team.owner_id,
        name: team.name,
        deleted_at: new Date().toISOString(),
        roles: [],
      };
      const url = "/api/v1/teams/" + team.id;
      Ajax.put(url, function () {
        Overlay.close();
        for (let i = 0; i < App.teams.length; i++) {
          const el = App.teams[i];
          if (el.id === team.id) {
            App.teams.splice(i, 1);
          }
        }
        const path = "/viz";
        history.pushState({}, "", path);
        App.router.match();
      }, changes);
    })
    return overlay;
  }

  static leaveTeamOverlay(team: ApiTeam, role: ApiTeamRole): Overlay {
    const d = div("overlay-panel-body");
    d.style.maxWidth = (window.innerWidth - 200) + "px";

    const settings = div("message");
    const message = document.createElement("p");
    message.innerHTML = "Are you sure you want to leave <b>" + team.name + "</b>? You will permamently lose access to everying in the team.";
    settings.appendChild(message);
    d.appendChild(settings);

    const panel_footer = div("overlay-panel-footer");
    const button = document.createElement("a");
    button.classList.add("button", "noselect", "destructive");
    panel_footer.appendChild(button);
    d.appendChild(panel_footer);

    const overlay = new Overlay(OverlayStyle.Viz, "Leave Team", [d], "Leave team", function () {
      const url = "/api/v1/team_roles/" + role.id;
      Ajax.delete(url, function () {
        //Overlay.clearAllOverlays();
        Overlay.close();
        for (let i = 0; i < App.teams.length; i++) {
          const el = App.teams[i];
          if (el.id === team.id) {
            App.teams.splice(i, 1);
          }
        }
        if (App.user) {
          App.selected_owner_id = App.user.id;
        }
        App.render();
      });
    });

    return overlay;
  }

  static addTeamMemberOverlay(team: ApiTeam, cb: Function): Overlay {

    const d = div("overlay-panel-body");
    d.style.maxWidth = (window.innerWidth - 200) + "px";

    const group = div("settings-group");
    const invite = div("section", "member-invite");
    const email_input = document.createElement("input");
    email_input.classList.add("email");
    email_input.placeholder = "Email";
    invite.appendChild(email_input);

    const options_wrapper = div("member-permissions");
    let role = "Editor";
    const render_selector = function (selected: string) {
      const options = [new DropdownOption("Admin", 0), new DropdownOption("Editor", 1)];
      const selector = viewDropdown(selected, options, function (option: DropdownOption<number>) {
        selector.classList.remove("active");
        role = option.label;
        render_selector(role);
      });
      options_wrapper.innerHTML = "";
      options_wrapper.appendChild(selector);
    };

    render_selector(role);
    invite.appendChild(options_wrapper);

    group.appendChild(invite);
    d.appendChild(group);

    const panel_footer = div("overlay-panel-footer");
    panel_footer.appendChild(div("spacer"));
    const button = document.createElement("a");
    button.classList.add("button", "noselect");
    button.innerHTML = "Send Invite";
    panel_footer.appendChild(button);
    d.appendChild(panel_footer);

    const send = function () {
      if (button.classList.contains("disabled")) {
        return;
      }
      button.classList.add("disabled");
      button.innerHTML = "Sending...";
      const add: ApiTeamAddUsersRequest = {
        email: email_input.value,
        role: role,
      };
      const url = "/api/v1/teams/" + team.id + "/add_users";
      Ajax.post(url, function (data: ApiTeamRole[]) {
        for (const r of data) {
          for (const t of App.teams) {
            if (t.id === team.id) {
              t.roles.push(r);
            }
          }
        }
        Overlay.close();
        cb(data);
      }, [add], function () {
        button.classList.remove("disabled");
        button.innerHTML = "Send";
      });
    };

    button.onclick = function () {
      send();
    }

    const overlay = new Overlay(OverlayStyle.Viz, "Add member", [d], "Send Invite", function () {
    }, undefined, function () {
      email_input.focus();
    });
    return overlay;
  }

  static userIsAdmin(user: ApiUser | null, team: ApiTeam): boolean {
    if (!user) {
      return false;
    }
    for (const r of team.roles) {
      if (r.user_id === user.id && r.role === "Admin") {
        return true;
      }
    }
    return false;
  }

  static teamMembersOverlay(team: ApiTeam): Overlay {
    const d = div("overlay-panel-body", "team-member-list");
    d.style.maxWidth = (window.innerWidth - 300) + "px";

    const settings = div("settings-group");
    const header = div("team-row", "team-row-header");
    const title_name = div("member-info");
    title_name.innerHTML = "Member";
    const title_role = div("permissions-info");
    title_role.innerHTML = "Permissions";
    header.appendChild(title_name);
    header.appendChild(title_role);
    settings.appendChild(header);

    const is_admin = Teams.userIsAdmin(App.user, team);

    const add_member_row = function (r: ApiTeamRole) {
      const row = div("team-row");
      const info = div("member-info");
      const user_info = div("team-member");
      const img = div("img");
      if (r.user && r.user.image_timestamp !== null) {
        // TODO
        //const url = r.user.uploaded_avatar;
        //img.style.backgroundImage = "url(" + url + ")";
      }
      user_info.appendChild(img);
      const name = div("name");
      name.innerHTML = r.email;
      if (App.user && r.user_id === App.user.id) {
        const s = span();
        s.innerHTML = " (You)";
        name.appendChild(s);
      }
      if (r.pending) {
        const s = span();
        s.innerHTML = " (Pending)";
        name.appendChild(s);
      }
      user_info.appendChild(name);
      info.appendChild(user_info);
      row.appendChild(info);

      const permissions = div("permissions-info");
      const is_owner = r.user_id === team.owner_id;
      const options_wrapper = div("member-permissions");
      let role = is_owner ? "Owner" : r.role;
      const render_selector = function (selected: string) {
        const options = [new DropdownOption("Admin", 0), new DropdownOption("Editor", 1), new DropdownOption("", -1, DropdownOptionType.Divider), new DropdownOption("Remove", 2)];
        const selector = viewDropdown(selected, options, function (option: DropdownOption<number>) {
          if (option.value === 0 || option.value === 1) {
            // update
            const change: ApiTeamRole = {
              id: r.id,
              team_id: r.team_id,
              user_id: r.user_id,
              user: null,
              pending: r.pending,
              email: r.email,
              role: option.label,
            };
            const url = "/api/v1/team_roles/" + r.id;
            Ajax.put(url, function () {
              selector.classList.remove("active");
              role = option.label;
              render_selector(role);
              for (const t of App.teams) {
                if (t.id === team.id) {
                  for (let i = 0; i < t.roles.length; i++) {
                    const el = t.roles[i];
                    if (el.id === r.id) {
                      t.roles[i].role = option.label;
                    }
                  }
                }
              }
            }, change);
          } else if (option.value === 2) {
            // remove
            const url = "/api/v1/team_roles/" + r.id;
            Ajax.delete(url, function () {
              for (const t of App.teams) {
                if (t.id === team.id) {
                  for (let i = 0; i < t.roles.length; i++) {
                    const el = t.roles[i];
                    if (el.id === r.id) {
                      t.roles.splice(i, 1);
                    }
                  }
                }
              }
              if (row.parentElement) {
                row.parentElement.removeChild(row);
              }
              //Api.getUser();
            });
          }
        });
        options_wrapper.innerHTML = "";
        options_wrapper.appendChild(selector);

        if (!is_admin || is_owner) {
          const input = selector.querySelector("input");
          if (input) {
            input.disabled = true;
          }
        }
        if (App.user && App.user.id === r.user_id && !is_owner) {
          const leave = anchor("team-leave");
          leave.innerHTML = "Leave team";
          options_wrapper.appendChild(leave);

          leave.onclick = function (e) {
            e.preventDefault();
            Teams.leaveTeamOverlay(team, r);
          };
        }
      };

      render_selector(role);
      permissions.appendChild(options_wrapper);

      row.appendChild(permissions);
      settings.appendChild(row);
    };

    for (const role of team.roles) {
      add_member_row(role);
    }
    d.appendChild(settings);

    const panel_footer = div("overlay-panel-footer", "team-footer");

    if (is_admin) {
      const add_member_wrapper = div("add-team-member");
      const add_member = div("team-member");
      const add_img = div("img");
      add_img.innerHTML = "+";
      add_member.appendChild(add_img);
      const add_name = div("name");
      add_name.innerHTML = "Add member";
      add_member.appendChild(add_name);
      add_member_wrapper.appendChild(add_member);
      panel_footer.appendChild(add_member_wrapper);

      const space = div("spacer");
      panel_footer.appendChild(space);

      add_member.onclick = function () {
        Teams.addTeamMemberOverlay(team, function (roles: ApiTeamRole[]) {
          for (const r of roles) {
            add_member_row(r);
          }
        });
      };
    }

    const button = document.createElement("a");
    button.classList.add("button", "noselect");
    button.innerHTML = "Done";
    button.onclick = function () {
      Overlay.close();
    };
    panel_footer.appendChild(button);
    d.appendChild(panel_footer);

    const overlay = new Overlay(OverlayStyle.Viz, team.name, [d], "Done", function () {
      Overlay.close();
    });
    overlay.panel.classList.add("flex-width");

    return overlay;
  }

}